import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './HuntScreen.css';

const GAME_WIDTH = 800;
const GAME_HEIGHT = 500;
const AGENT_SIZE = 40;

// Base speeds (pixels per second at 60fps baseline)
const TARGET_FPS = 60;
const BULLET_SPEED = 8 * TARGET_FPS;        // 480 pixels/sec
const ENEMY_BULLET_SPEED = 4 * TARGET_FPS;  // 240 pixels/sec
const AGENT_SPEED = 6 * TARGET_FPS;         // 360 pixels/sec
const BOSS_ENTRY_SPEED = 2 * TARGET_FPS;    // 120 pixels/sec
const BOSS_MOVE_SPEED = 2.5 * TARGET_FPS;   // 150 pixels/sec
const ENEMY_DROP = 15;                       // pixels per edge hit (instant)

const MALICIOUS_CODE = [
  "' OR 1=1--", "<script>", "eval(input)", "rm -rf /", "DROP TABLE",
  "${user.pw}", "../../etc", "exec(cmd)", "pickle.loads", "innerHTML=",
];

const MITIGATION_CODE = [
  "sanitize()", "validate()", "escape()", "parameterize", "encrypt()",
  "hashPwd()", "checkAuth()", "rateLimit()", "allowList()", "encode()",
];

// Difficulty settings based on OWASP rank (10 = easiest, 1 = hardest)
// baseSpeed is now a multiplier
const getDifficultySettings = (rank) => {
  if (rank >= 8) return { rows: 2, baseSpeed: 1.0 + (10 - rank) * 0.15, fireRate: 0.6, eliteCount: 0, bossPhases: 1 };
  if (rank >= 5) return { rows: 3, baseSpeed: 1.4 + (7 - rank) * 0.15, fireRate: 0.9, eliteCount: 1, bossPhases: 2 };
  if (rank >= 2) return { rows: 4, baseSpeed: 1.8 + (4 - rank) * 0.15, fireRate: 1.2, eliteCount: 2, bossPhases: 2 };
  return { rows: 5, baseSpeed: 2.4, fireRate: 1.5, eliteCount: 3, bossPhases: 2 };
};

// ============================================================================
// CHIPTUNE AUDIO ENGINE
// ============================================================================
class ChiptuneAudio {
  constructor() {
    this.ctx = null;
    this.musicGain = null;
    this.sfxGain = null;
    this.musicPlaying = false;
    this.musicInterval = null;
  }

  init() {
    if (this.ctx) return;
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = 0.15;
    this.musicGain.connect(this.ctx.destination);
    this.sfxGain = this.ctx.createGain();
    this.sfxGain.gain.value = 0.25;
    this.sfxGain.connect(this.ctx.destination);
  }

  playTone(freq, duration, type = 'square', gain = this.sfxGain) {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0.3, this.ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + duration);
    osc.connect(g);
    g.connect(gain);
    osc.start();
    osc.stop(this.ctx.currentTime + duration);
  }

  shoot() {
    this.init();
    this.playTone(880, 0.05, 'square');
    setTimeout(() => this.playTone(1100, 0.05, 'square'), 30);
  }

  hit() {
    this.init();
    this.playTone(200, 0.1, 'sawtooth');
    this.playTone(150, 0.15, 'square');
  }

  enemyDie() {
    this.init();
    this.playTone(400, 0.05, 'square');
    setTimeout(() => this.playTone(500, 0.05, 'square'), 40);
    setTimeout(() => this.playTone(600, 0.08, 'square'), 80);
  }

  eliteDie() {
    this.init();
    [400, 500, 600, 800, 1000].forEach((f, i) => {
      setTimeout(() => this.playTone(f, 0.08, 'square'), i * 50);
    });
  }

  playerHit() {
    this.init();
    this.playTone(100, 0.2, 'sawtooth');
    this.playTone(80, 0.3, 'square');
  }

  dodge() {
    this.init();
    this.playTone(600, 0.05, 'triangle');
    this.playTone(800, 0.05, 'triangle');
  }

  breach() {
    this.init();
    [200, 400, 200, 400].forEach((f, i) => {
      setTimeout(() => this.playTone(f, 0.15, 'sawtooth'), i * 100);
    });
  }

  bossAppear() {
    this.init();
    [200, 250, 300, 350, 400].forEach((f, i) => {
      setTimeout(() => this.playTone(f, 0.15, 'square'), i * 100);
    });
  }

  bossPhaseChange() {
    this.init();
    this.playTone(150, 0.3, 'sawtooth');
    setTimeout(() => this.playTone(200, 0.2, 'square'), 150);
    setTimeout(() => this.playTone(300, 0.2, 'square'), 300);
  }

  combo() {
    this.init();
    this.playTone(1200, 0.05, 'square');
    this.playTone(1400, 0.05, 'square');
  }

  comboLost() {
    this.init();
    this.playTone(200, 0.15, 'sawtooth');
  }

  victory() {
    this.init();
    [523, 659, 784, 1047].forEach((f, i) => {
      setTimeout(() => this.playTone(f, 0.2, 'square'), i * 150);
    });
  }

  gameOver() {
    this.init();
    [400, 350, 300, 200].forEach((f, i) => {
      setTimeout(() => this.playTone(f, 0.25, 'sawtooth'), i * 200);
    });
  }

  startMusic() {
    this.init();
    if (this.musicPlaying) return;
    this.musicPlaying = true;
    const bassNotes = [110, 130.81, 146.83, 130.81];
    const melodyNotes = [220, 261.63, 293.66, 329.63, 293.66, 261.63];
    let bassIdx = 0, melodyIdx = 0, beat = 0;
    this.musicInterval = setInterval(() => {
      if (!this.musicPlaying) return;
      if (beat % 4 === 0) {
        this.playTone(bassNotes[bassIdx % bassNotes.length], 0.15, 'triangle', this.musicGain);
        bassIdx++;
      }
      if (beat % 2 === 0) {
        this.playTone(melodyNotes[melodyIdx % melodyNotes.length], 0.1, 'square', this.musicGain);
        melodyIdx++;
      }
      beat++;
    }, 150);
  }

  stopMusic() {
    this.musicPlaying = false;
    if (this.musicInterval) {
      clearInterval(this.musicInterval);
      this.musicInterval = null;
    }
  }
}

const audio = new ChiptuneAudio();

// ============================================================================
// HUNT SCREEN COMPONENT
// ============================================================================
const HuntScreen = ({
  vuln,
  levelNum,
  totalLevels,
  score,
  credibility,
  onComplete,
  addScore
}) => {
  const difficulty = useMemo(() => getDifficultySettings(vuln.rank), [vuln.rank]);

  const gameRef = useRef(null);
  const lastTimeRef = useRef(0);
  const [initialized, setInitialized] = useState(false);
  const [showReady, setShowReady] = useState(true);
  const [, forceRender] = useState(0);
  const [message, setMessage] = useState(null);
  const [playerHP, setPlayerHP] = useState(3);
  const [playerCredibility, setPlayerCredibility] = useState(0);
  const [combo, setCombo] = useState(0);
  const [comboTimer, setComboTimer] = useState(0);
  const [bossPhase, setBossPhase] = useState(1);
  const [bossHP, setBossHP] = useState(100);

  const showMsg = useCallback((text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 500);
  }, []);

  const getMaliciousCode = () => MALICIOUS_CODE[Math.floor(Math.random() * MALICIOUS_CODE.length)];
  const getMitigationCode = () => MITIGATION_CODE[Math.floor(Math.random() * MITIGATION_CODE.length)];

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Initialize level
  useEffect(() => {
    const threats = vuln.hunt.threats;
    const COLS = 5;
    const ROWS = difficulty.rows;
    const totalEnemies = COLS * ROWS;

    let threatPool = [];
    while (threatPool.length < totalEnemies) {
      threatPool = threatPool.concat(threats);
    }
    threatPool = shuffleArray(threatPool.slice(0, totalEnemies));

    const allPositions = [];
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        allPositions.push({ row, col });
      }
    }
    const shuffledPositions = shuffleArray(allPositions);
    const elitePositions = new Set(
      shuffledPositions.slice(0, difficulty.eliteCount).map(p => `${p.row}-${p.col}`)
    );

    const enemies = [];
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const idx = row * COLS + col;
        const isElite = elitePositions.has(`${row}-${col}`);
        enemies.push({
          id: `e-${row}-${col}`,
          x: 80 + col * 130,
          y: 40 + row * 50,
          width: 100,
          height: 40,
          label: threatPool[idx].label,
          points: isElite ? 500 : (threatPool[idx].points || 100),
          isElite,
          hp: isElite ? 3 : 1
        });
      }
    }

    const shields = ['validate()', 'sanitize()', 'encrypt()', 'auth()'].map((code, i) => ({
      id: `s-${i}`,
      x: 70 + i * 185,
      y: GAME_HEIGHT - 140,
      width: 100,
      height: 30,
      hp: 3,
      code
    }));

    gameRef.current = {
      phase: 'ready',
      agentX: GAME_WIDTH / 2 - AGENT_SIZE / 2,
      bullets: [],
      enemyBullets: [],
      enemies,
      shields,
      boss: null,
      bossHP: 100,
      bossPhase: 1,
      bossMaxPhases: difficulty.bossPhases,
      playerHP: 3,
      playerCredibility: 0,
      enemyDir: 1,
      enemySpeedMult: difficulty.baseSpeed,
      baseFireRate: difficulty.fireRate,
      combo: 0,
      comboTimer: 0,
      maxCombo: 0,
      fireAccumulator: 0,
      stats: { threatsKilled: 0, points: 0, died: false, quit: false, maxCombo: 0, dodges: 0 },
      keys: {},
      lastShot: 0,
      hitFlash: 0,
      dodgeCooldown: 0
    };

    lastTimeRef.current = 0;
    setPlayerHP(3);
    setPlayerCredibility(0);
    setCombo(0);
    setComboTimer(0);
    setBossPhase(1);
    setBossHP(100);
    setShowReady(true);
    setInitialized(true);

    return () => {
      audio.stopMusic();
      setInitialized(false);
    };
  }, [vuln.id, vuln.hunt.threats, difficulty]);

  const dismissReady = useCallback(() => {
    if (gameRef.current) {
      gameRef.current.phase = 'hunt';
      setShowReady(false);
      audio.startMusic();
    }
  }, []);

  // Keyboard handlers
  useEffect(() => {
    if (!initialized || !gameRef.current) return;

    const onKeyDown = (e) => {
      const g = gameRef.current;
      if (!g) return;

      if (g.phase === 'ready') {
        e.preventDefault();
        dismissReady();
        return;
      }

      g.keys[e.key] = true;
      if (e.key === ' ' && g.phase !== 'victory' && g.phase !== 'ready') {
        e.preventDefault();
        const now = Date.now();
        if (now - g.lastShot > 200) {
          g.lastShot = now;
          g.bullets.push({
            id: now,
            x: g.agentX + AGENT_SIZE / 2,
            y: GAME_HEIGHT - 75,
            code: getMitigationCode()
          });
          audio.shoot();
        }
      }
    };

    const onKeyUp = (e) => {
      const g = gameRef.current;
      if (!g) return;
      g.keys[e.key] = false;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [initialized, dismissReady]);

  // Main game loop with delta time
  useEffect(() => {
    if (!initialized || !gameRef.current) return;

    let animationId;

    const loop = (currentTime) => {
      const g = gameRef.current;
      if (!g) return;

      // Calculate delta time (in seconds)
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = currentTime;
      }
      const deltaTime = Math.min((currentTime - lastTimeRef.current) / 1000, 0.1); // Cap at 100ms to prevent huge jumps
      lastTimeRef.current = currentTime;

      if (g.phase === 'victory' || g.phase === 'ready') {
        forceRender(n => n + 1);
        animationId = requestAnimationFrame(loop);
        return;
      }

      // Frame-based timers converted to time-based
      const frameEquivalent = deltaTime * TARGET_FPS;
      
      if (g.hitFlash > 0) g.hitFlash = Math.max(0, g.hitFlash - frameEquivalent);
      if (g.dodgeCooldown > 0) g.dodgeCooldown = Math.max(0, g.dodgeCooldown - frameEquivalent);

      // Combo timer decay (was 90 frames = 1.5 seconds)
      if (g.comboTimer > 0) {
        g.comboTimer = Math.max(0, g.comboTimer - frameEquivalent);
        setComboTimer(g.comboTimer);
        if (g.comboTimer <= 0 && g.combo > 0) {
          audio.comboLost();
          g.combo = 0;
          setCombo(0);
        }
      }

      // Move agent
      const isMoving = g.keys['ArrowLeft'] || g.keys['a'] || g.keys['ArrowRight'] || g.keys['d'];
      const agentMove = AGENT_SPEED * deltaTime;
      if (g.keys['ArrowLeft'] || g.keys['a']) g.agentX = Math.max(10, g.agentX - agentMove);
      if (g.keys['ArrowRight'] || g.keys['d']) g.agentX = Math.min(GAME_WIDTH - AGENT_SIZE - 10, g.agentX + agentMove);

      // Move bullets
      const bulletMove = BULLET_SPEED * deltaTime;
      const enemyBulletMove = ENEMY_BULLET_SPEED * deltaTime;
      g.bullets = g.bullets.filter(b => { b.y -= bulletMove; return b.y > -30; });
      g.enemyBullets = g.enemyBullets.filter(b => { b.y += enemyBulletMove; return b.y < GAME_HEIGHT + 30; });

      // HUNT PHASE
      if (g.phase === 'hunt' && g.enemies.length > 0) {
        let hitEdge = false;
        let breached = false;

        for (const e of g.enemies) {
          if (g.enemyDir === 1 && e.x + e.width >= GAME_WIDTH - 15) hitEdge = true;
          if (g.enemyDir === -1 && e.x <= 15) hitEdge = true;
          if (e.y + e.height >= GAME_HEIGHT - 80) {
            breached = true;
          }
        }

        if (breached) {
          audio.breach();
          showMsg('SECURITY BREACH!', 'error');
          g.phase = 'victory';
          g.stats.died = true;
          audio.stopMusic();
          audio.gameOver();
          forceRender(n => n + 1);
          return;
        }

        if (hitEdge) {
          g.enemyDir *= -1;
          g.enemies.forEach(e => e.y += ENEMY_DROP);
        } else {
          // Enemy movement: baseSpeed * 60 pixels per second * speedMult * deltaTime
          const enemyMove = 60 * g.enemySpeedMult * deltaTime;
          g.enemies.forEach(e => e.x += enemyMove * g.enemyDir);
        }

        // Fire rate: accumulate time and fire when threshold reached
        g.fireAccumulator += deltaTime;
        const fireThreshold = 1 / g.baseFireRate; // seconds between shots
        if (g.fireAccumulator >= fireThreshold && g.enemies.length > 0) {
          g.fireAccumulator = 0;
          const shooter = g.enemies[Math.floor(Math.random() * g.enemies.length)];
          g.enemyBullets.push({
            id: Date.now() + Math.random(),
            x: shooter.x + shooter.width / 2,
            y: shooter.y + shooter.height,
            code: getMaliciousCode(),
            color: shooter.isElite ? 'yellow' : 'red'
          });
        }
      }

      // BOSS PHASE
      if (g.phase === 'boss' && g.boss) {
        const phaseSpeedMult = g.bossPhase === 2 ? 1.5 : 1;
        
        if (g.boss.y < 50) {
          g.boss.y += BOSS_ENTRY_SPEED * deltaTime;
        } else {
          // Move boss
          const bossMove = BOSS_MOVE_SPEED * phaseSpeedMult * deltaTime;
          g.boss.x += g.boss.dir * bossMove;
          
          // Boundary checks with margin
          const leftBound = 50;
          const rightBound = GAME_WIDTH - g.boss.width - 50;
          
          if (g.boss.x <= leftBound) {
            g.boss.x = leftBound;
            g.boss.dir = 1;
          } else if (g.boss.x >= rightBound) {
            g.boss.x = rightBound;
            g.boss.dir = -1;
          }
        }

        // Boss shooting - time based
        const shootChance = (g.bossPhase === 2 ? 2.1 : 1.2) * deltaTime; // shots per second * deltaTime
        if (g.boss.y >= 50 && Math.random() < shootChance) {
          const bulletsToShoot = g.bossPhase === 2 ? 3 : 1;
          const spread = 40;
          const bulletColor = g.bossPhase === 2 ? 'red' : 'yellow';
          for (let i = 0; i < bulletsToShoot; i++) {
            const offset = (i - (bulletsToShoot - 1) / 2) * spread;
            g.enemyBullets.push({
              id: Date.now() + i,
              x: g.boss.x + g.boss.width / 2 + offset,
              y: g.boss.y + g.boss.height,
              code: getMaliciousCode(),
              color: bulletColor
            });
          }
        }
      }

      // COLLISION: Player bullets vs enemies
      g.bullets = g.bullets.filter(bullet => {
        for (let i = g.enemies.length - 1; i >= 0; i--) {
          const e = g.enemies[i];
          if (bullet.x >= e.x && bullet.x <= e.x + e.width &&
              bullet.y >= e.y && bullet.y <= e.y + e.height) {
            e.hp--;
            if (e.hp <= 0) {
              g.enemies.splice(i, 1);
              g.stats.threatsKilled++;

              g.combo = Math.min(g.combo + 1, 4);
              g.comboTimer = 90; // 1.5 seconds worth of frames
              if (g.combo > g.maxCombo) g.maxCombo = g.combo;
              g.stats.maxCombo = g.maxCombo;
              setCombo(g.combo);
              setComboTimer(g.comboTimer);
              if (g.combo > 1) audio.combo();

              const multiplier = g.combo;
              const points = e.points * multiplier;
              g.stats.points += points;
              addScore(points);
              showMsg(g.combo > 1 ? `+${points} x${multiplier}` : `+${points}`, 'success');

              // Speed increase (capped)
              g.enemySpeedMult = Math.min(g.enemySpeedMult + 0.05, 4);
              g.playerCredibility = Math.min(100, g.playerCredibility + (e.isElite ? 20 : 10));
              setPlayerCredibility(g.playerCredibility);

              if (e.isElite) audio.eliteDie();
              else audio.enemyDie();

              if (g.enemies.length === 0) {
                g.phase = 'boss';
                g.boss = { 
                  x: GAME_WIDTH / 2 - 100, 
                  y: -100, 
                  width: 200, 
                  height: 80, 
                  dir: Math.random() > 0.5 ? 1 : -1
                };
                g.bossHP = 100;
                setBossHP(100);
                g.bossPhase = 1;
                setBossPhase(1);
                audio.bossAppear();
              }
            } else {
              showMsg('HIT!', 'success');
              audio.hit();
            }
            return false;
          }
        }
        return true;
      });

      // COLLISION: Player bullets vs boss
      if (g.phase === 'boss' && g.boss) {
        g.bullets = g.bullets.filter(bullet => {
          if (!g.boss) return true;
          if (bullet.x >= g.boss.x && bullet.x <= g.boss.x + g.boss.width &&
              bullet.y >= g.boss.y && bullet.y <= g.boss.y + g.boss.height) {
            g.bossHP -= 10;
            setBossHP(g.bossHP);

            g.combo = Math.min(g.combo + 1, 4);
            g.comboTimer = 90;
            if (g.combo > g.maxCombo) g.maxCombo = g.combo;
            g.stats.maxCombo = g.maxCombo;
            setCombo(g.combo);
            setComboTimer(g.comboTimer);

            const multiplier = g.combo;
            const points = 100 * multiplier;
            addScore(points);
            showMsg(g.combo > 1 ? `-10HP x${multiplier}` : '-10 HP', 'success');
            audio.hit();

            g.playerCredibility = Math.min(100, g.playerCredibility + 5);
            setPlayerCredibility(g.playerCredibility);

            // Phase transition: at 50% HP, if boss has 2 phases, transform
            if (g.bossHP <= 50 && g.bossPhase === 1 && g.bossMaxPhases >= 2) {
              g.bossPhase = 2;
              g.bossHP = 100;
              setBossHP(100);
              setBossPhase(2);
              audio.bossPhaseChange();
              showMsg('PHASE 2!', 'error');
            }

            if (g.bossHP <= 0) {
              g.phase = 'victory';
              g.boss = null;
              addScore(vuln.bounty);
              g.stats.points += vuln.bounty;
              audio.stopMusic();
              audio.victory();
            }
            return false;
          }
          return true;
        });
      }

      // COLLISION: Player bullets vs shields
      g.bullets = g.bullets.filter(bullet => {
        for (let i = g.shields.length - 1; i >= 0; i--) {
          const s = g.shields[i];
          if (bullet.x >= s.x && bullet.x <= s.x + s.width &&
              bullet.y >= s.y && bullet.y <= s.y + s.height) {
            s.hp--;
            if (s.hp <= 0) g.shields.splice(i, 1);
            return false;
          }
        }
        return true;
      });

      // COLLISION: Enemy bullets vs shields
      g.enemyBullets = g.enemyBullets.filter(bullet => {
        for (let i = g.shields.length - 1; i >= 0; i--) {
          const s = g.shields[i];
          if (bullet.x >= s.x && bullet.x <= s.x + s.width &&
              bullet.y >= s.y && bullet.y <= s.y + s.height) {
            s.hp--;
            if (s.hp <= 0) g.shields.splice(i, 1);
            return false;
          }
        }
        return true;
      });

      // COLLISION: Enemy bullets vs player
      const pLeft = g.agentX - 5;
      const pRight = g.agentX + AGENT_SIZE + 5;
      const pTop = GAME_HEIGHT - 75;
      const pBot = GAME_HEIGHT - 15;
      
      // Wider dodge detection zone
      const dodgeLeft = g.agentX - 20;
      const dodgeRight = g.agentX + AGENT_SIZE + 20;
      const dodgeTop = GAME_HEIGHT - 85;
      const dodgeBot = GAME_HEIGHT - 5;

      g.enemyBullets = g.enemyBullets.filter(bullet => {
        // Check for direct hit
        if (bullet.x >= pLeft && bullet.x <= pRight && bullet.y >= pTop && bullet.y <= pBot) {
          g.playerHP--;
          g.hitFlash = 30;
          setPlayerHP(g.playerHP);
          showMsg('DAMAGE!', 'error');
          audio.playerHit();

          if (g.combo > 0) {
            g.combo = 0;
            g.comboTimer = 0;
            setCombo(0);
            setComboTimer(0);
            audio.comboLost();
          }

          g.playerCredibility = Math.max(0, g.playerCredibility - 15);
          setPlayerCredibility(g.playerCredibility);

          if (g.playerHP <= 0) {
            g.phase = 'victory';
            g.stats.died = true;
            audio.stopMusic();
            audio.gameOver();
          }
          return false;
        }
        
        // Check for dodge (bullet in dodge zone but not hit zone, and player is moving)
        if (isMoving && g.dodgeCooldown <= 0 &&
            bullet.x >= dodgeLeft && bullet.x <= dodgeRight && 
            bullet.y >= dodgeTop && bullet.y <= dodgeBot &&
            !(bullet.x >= pLeft && bullet.x <= pRight && bullet.y >= pTop && bullet.y <= pBot)) {
          g.stats.dodges++;
          g.dodgeCooldown = 30;
          showMsg('DODGE!', 'dodge');
          audio.dodge();
        }
        
        return true;
      });

      forceRender(n => n + 1);
      animationId = requestAnimationFrame(loop);
    };

    animationId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animationId);
  }, [initialized, addScore, showMsg, vuln.bounty]);

  const handleNext = () => {
    audio.stopMusic();
    if (gameRef.current) {
      onComplete(gameRef.current.stats);
    }
  };

  const handleAbandon = () => {
    audio.stopMusic();
    if (gameRef.current) {
      gameRef.current.stats.quit = true;
      onComplete(gameRef.current.stats);
    }
  };

  if (!initialized || !gameRef.current) {
    return (
      <div className="hunt-screen">
        <div className="loading">Initializing...</div>
      </div>
    );
  }

  const g = gameRef.current;
  const comboPercent = (comboTimer / 90) * 100;

  return (
    <div className="hunt-screen">
      <div className="hunt-header">
        <div className="hunt-info">
          <span className="level-tag">BOUNTY {levelNum}/{totalLevels}</span>
          <span className="target-tag">{vuln.id}: {vuln.shortName}</span>
        </div>
        <div className="hunt-stats">
          <span>SCORE: {score}</span>
          {combo > 1 && (
            <span className="combo-display">
              COMBO x{combo}
              <div className="combo-timer" style={{ width: `${comboPercent}%` }} />
            </span>
          )}
        </div>
      </div>

      <div className="game-area" style={{ width: GAME_WIDTH, height: GAME_HEIGHT }}>
        {showReady && (
          <div className="ready-overlay" onClick={dismissReady}>
            <div className="ready-content">
              <h2>GET READY!</h2>
              <div className="ready-controls">
                <div className="control-row">
                  <span className="control-key">← →</span>
                  <span className="control-desc">or</span>
                  <span className="control-key">A D</span>
                  <span className="control-desc">MOVE</span>
                </div>
                <div className="control-row">
                  <span className="control-key">SPACE</span>
                  <span className="control-desc">FIRE</span>
                </div>
              </div>
              <p className="ready-prompt">PRESS ANY KEY TO START</p>
            </div>
          </div>
        )}

        {message && <div className={`game-message ${message.type}`}>{message.text}</div>}
        {g.hitFlash > 0 && <div className="hit-flash" />}

        {g.enemies.map(e => (
          <div
            key={e.id}
            className={`enemy ${e.isElite ? 'elite' : ''} ${e.hp < 3 && e.isElite ? `damage-${3 - e.hp}` : ''}`}
            style={{ left: e.x, top: e.y, width: e.width, height: e.height }}
          >
            <span className="enemy-label">{e.label}</span>
            {e.isElite && <div className="elite-hp">{'●'.repeat(e.hp)}{'○'.repeat(3 - e.hp)}</div>}
          </div>
        ))}

        {g.phase === 'boss' && g.boss && (
          <div
            className={`boss phase-${bossPhase}`}
            style={{ left: g.boss.x, top: g.boss.y, width: g.boss.width, height: g.boss.height }}
          >
            <div className="boss-hp-bar">
              <div className="boss-hp-fill" style={{ width: `${bossHP}%` }} />
            </div>
            <div className="boss-body">
              <span className="boss-name">☠ {vuln.shortName} ☠</span>
              {bossPhase > 1 && <span className="boss-phase-label">ENRAGED!</span>}
            </div>
          </div>
        )}

        {g.shields.map(s => (
          <div key={s.id} className={`shield hp-${s.hp}`} style={{ left: s.x, top: s.y, width: s.width, height: s.height }}>
            <code>{s.code}</code>
          </div>
        ))}

        {g.bullets.map(b => (
          <div key={b.id} className="code-bullet player-code" style={{ left: b.x, top: b.y }}>{b.code}</div>
        ))}

        {g.enemyBullets.map(b => (
          <div key={b.id} className={`code-bullet enemy-code ${b.color === 'yellow' ? 'yellow-code' : ''}`} style={{ left: b.x, top: b.y }}>{b.code}</div>
        ))}

        <div className={`agent ${g.hitFlash > 0 ? 'hit' : ''}`} style={{ left: g.agentX }}>
          <pre>{`  ▲\n ███\n/███\\`}</pre>
        </div>

        {g.phase === 'victory' && (
          <div className="victory-overlay">
            {g.stats.died ? (
              <>
                <h2 className="defeat-title">AGENT DOWN!</h2>
                <p>Mission failed on {vuln.id}</p>
                <p>Threats neutralized: {g.stats.threatsKilled}</p>
                {g.maxCombo > 1 && <p>Max Combo: x{g.maxCombo}</p>}
                {g.stats.dodges > 0 && <p>Dodges: {g.stats.dodges}</p>}
              </>
            ) : (
              <>
                <h2 className="victory-title">BOUNTY COLLECTED!</h2>
                <p>{vuln.id}: {vuln.name} mitigated</p>
                <p className="bounty-points">+{vuln.bounty} pts</p>
                {g.maxCombo > 1 && <p>Max Combo: x{g.maxCombo}</p>}
                {g.stats.dodges > 0 && <p>Dodges: {g.stats.dodges}</p>}
              </>
            )}
            <div className="victory-buttons">
              <button className="btn abandon-btn" onClick={handleAbandon}>[ ABANDON HUNT ]</button>
              <button className="btn next-btn" onClick={handleNext}>
                {g.stats.died ? '[ VIEW RESULTS ]' : levelNum >= totalLevels ? '[ FINAL RESULTS ]' : '[ NEXT BOUNTY ]'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="status-bars">
        <div className="status-bar-row">
          <span className="status-label">INTEGRITY:</span>
          <div className="status-bar">
            <div className={`status-fill integrity ${playerHP <= 1 ? 'critical' : playerHP <= 2 ? 'warning' : ''}`}
                 style={{ width: `${(playerHP / 3) * 100}%` }} />
          </div>
          <span className="status-value">{playerHP}/3</span>
        </div>
        <div className="status-bar-row">
          <span className="status-label">CREDIBILITY:</span>
          <div className="status-bar">
            <div className="status-fill credibility" style={{ width: `${playerCredibility}%` }} />
          </div>
          <span className="status-value">{playerCredibility}%</span>
        </div>
      </div>
    </div>
  );
};

export default HuntScreen;
